import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } });

// Auth is cookie-based (httpOnly). The access token is only mirrored in memory as a fallback for
// browsers that block cookies inside embedded previews; when cookies are confirmed blocked it is
// kept in sessionStorage (this tab only) so a reload does not sign the user out.
const TOKEN_KEY = "tutorials.session_token";
let memoryToken = sessionStorage.getItem(TOKEN_KEY) || null;
export const setAccessToken = (token, { persist = false } = {}) => {
  memoryToken = token || null;
  if (token && persist) sessionStorage.setItem(TOKEN_KEY, token);
  if (!token) sessionStorage.removeItem(TOKEN_KEY);
};

api.interceptors.request.use((config) => {
  if (memoryToken && !config.skipAuthHeader) config.headers.Authorization = `Bearer ${memoryToken}`;
  return config;
});

// True when the browser accepted our auth cookies (probe /auth/me without the bearer fallback).
export const cookiesWork = async () => {
  try {
    await api.get("/auth/me", { skipAuthHeader: true });
    return true;
  } catch {
    return false;
  }
};

export const resolveImage = (url) => (url && url.startsWith("/") ? `${BACKEND_URL}${url}` : url);

export function errorMessage(e, fallback = "Something went wrong. Please try again.") {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  if (typeof detail.message === "string") return detail.message;
  return String(detail);
}

export const BREATHING_PRESETS = {
  box: { name: "Box breathing (4-4-4-4)", inhale: 4, hold_in: 4, exhale: 4, hold_out: 4 },
  "478": { name: "4-7-8 relaxing breath", inhale: 4, hold_in: 7, exhale: 8, hold_out: 0 },
  calm: { name: "Calm breath (4 in, 6 out)", inhale: 4, hold_in: 0, exhale: 6, hold_out: 0 },
  equal: { name: "Equal breathing (5-5)", inhale: 5, hold_in: 0, exhale: 5, hold_out: 0 },
  custom: { name: "Custom pattern" },
};

export const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

export const MOODS = [
  { value: 1, label: "Very tense", color: "bg-rose-100 text-rose-700 border-rose-200", active: "bg-rose-600 text-white border-rose-600" },
  { value: 2, label: "Tense", color: "bg-amber-100 text-amber-700 border-amber-200", active: "bg-amber-500 text-white border-amber-500" },
  { value: 3, label: "Okay", color: "bg-slate-100 text-slate-700 border-slate-200", active: "bg-slate-700 text-white border-slate-700" },
  { value: 4, label: "Calm", color: "bg-sky-100 text-sky-700 border-sky-200", active: "bg-sky-600 text-white border-sky-600" },
  { value: 5, label: "Very calm", color: "bg-emerald-100 text-emerald-700 border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600" },
];
export const moodLabel = (v) => MOODS.find((m) => m.value === v)?.label || "—";

export const localDate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export async function uploadImage(file) {
  const form = new FormData();
  form.append("file", file);
  const r = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
}

export const difficultyClass = {
  Beginner: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Intermediate: "bg-amber-50 text-amber-700 border border-amber-200",
  Advanced: "bg-rose-50 text-rose-700 border border-rose-200",
};

export function videoEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace("www.", "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host.endsWith("youtube.com")) {
      if (u.searchParams.get("v")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
      const m = u.pathname.match(/\/(embed|shorts|live)\/([^/?]+)/);
      if (m) return `https://www.youtube.com/embed/${m[2]}`;
    }
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
      if (host === "player.vimeo.com") return url;
    }
  } catch (e) {
    return null;
  }
  return null;
}
